import { ONBOARDING_TOURS } from './catalog'
import { shouldSkipTourByStoredState } from './storage'
import type { OnboardingStateMap, OnboardingTourDefinition } from './types'
import { userHasCapability } from '~~/utils/auth/roleCapabilityMatrix'

export function hasTourCapabilities(tour: OnboardingTourDefinition, roles: string[]): boolean {
  return tour.requiredCapabilities.every(capability => userHasCapability(roles, capability))
}

function isTourEligibleOnRoute(
  tour: OnboardingTourDefinition,
  path: string,
  canEditOnCurrentRoute: boolean
): boolean {
  if (!tour.routeMatcher(path)) {
    return false
  }
  return !(tour.requiresEditOnRoute && !canEditOnCurrentRoute)
}

export function getAccessibleTours(roles: string[]): OnboardingTourDefinition[] {
  return ONBOARDING_TOURS.filter(tour => hasTourCapabilities(tour, roles))
}

function sortToursByCapabilityThenId(tours: OnboardingTourDefinition[]): OnboardingTourDefinition[] {
  return [...tours].sort((leftTour, rightTour) => {
    const leftKey = leftTour.requiredCapabilities.join('|')
    const rightKey = rightTour.requiredCapabilities.join('|')
    if (leftKey === rightKey) {
      return leftTour.id.localeCompare(rightTour.id)
    }
    return leftKey.localeCompare(rightKey)
  })
}

type TourSelectableState = {
  resolvedState: OnboardingStateMap
  disabledTourIds: ReadonlySet<string>
}

function isTourSelectable(
  tour: OnboardingTourDefinition,
  path: string,
  roles: string[],
  canEditOnCurrentRoute: boolean,
  selectionState: TourSelectableState
): boolean {
  const { resolvedState, disabledTourIds } = selectionState
  return (
    hasTourCapabilities(tour, roles) &&
    isTourEligibleOnRoute(tour, path, canEditOnCurrentRoute) &&
    !disabledTourIds.has(tour.id) &&
    !shouldSkipTourByStoredState(tour, resolvedState[tour.id])
  )
}

/** Sélectionne le premier tour éligible (droits, route, état déjà complété) sur la route courante. */
export function pickEligibleTour(
  path: string,
  roles: string[],
  canEditOnCurrentRoute: boolean,
  resolvedState: OnboardingStateMap,
  disabledTourIds: ReadonlySet<string> = new Set()
): OnboardingTourDefinition | null {
  const sortedTours = sortToursByCapabilityThenId(ONBOARDING_TOURS)
  const eligibleTour = sortedTours.find(tour =>
    isTourSelectable(tour, path, roles, canEditOnCurrentRoute, { resolvedState, disabledTourIds })
  )
  return eligibleTour ?? null
}
